//@flow
import React, {useState, useEffect} from 'react';
import {Link, useParams, Redirect} from 'react-router-dom';
import {makeStyles, Typography, Grid, Box, IconButton} from '@material-ui/core';
import {AlertTitle, Alert} from '@material-ui/lab';
import {colors} from '../../../assets/styles/colors';
import {ArrowBack} from '@material-ui/icons';
import LoadingVeil from '../../../elements/LoadingVeil/LoadingVeil';
import {useChatBot} from '../hooks';
import BotPromptSection from './BotPromptSection';
import TrainingSection from './TrainingSection';
import DeviceLinkingSection from './DeviceLinkingSection';
import ScheduleSection from './ScheduleSection';

const useStyles = makeStyles(theme => ({
  header: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: 12,
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    backgroundColor: colors.grey300,
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  container: {
    paddingInline: '.5rem',
    paddingTop: '1rem',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  root: {
    marginBottom: '2rem',
    maxWidth: '1200px',
  },
}));

export default function TrainingBot() {
  const classes = useStyles();
  const {botId} = useParams();

  const {chatBot, loading, refetchChatBot} = useChatBot(botId ?? '');

  const [alertErrorOpen, setAlertErrorOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState('');

  useEffect(() => {
    if (chatBot) {
      const failTraining = chatBot?.state === 'FAILED_TRAINING';

      if (failTraining) {
        setAlertErrorOpen(true);
        setErrorDetails(
          chatBot?.errorMessage ||
            'Hubo un problema durante el entrenamiento. Por favor intenta nuevamente.'
        );
      }
    }
  }, [chatBot]);

  if (loading && !chatBot) {
    return <LoadingVeil visible />;
  }

  if (!chatBot) {
    return <Redirect to="404" />;
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={12}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton
              className={classes.backButton}
              component={Link}
              to="/store-bot/bots"
            >
              <ArrowBack />
            </IconButton>
            <header className={classes.header}>Entrenamiento del bot</header>
          </Box>
        </Grid>

        <BotPromptSection chatBot={chatBot} onSaveSuccess={refetchChatBot} />

        {alertErrorOpen && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              onClose={() => {
                setAlertErrorOpen(false);
                setErrorDetails('');
              }}
            >
              <AlertTitle>¡No se pudo completar el entrenamiento!</AlertTitle>
              {errorDetails ||
                'Hubo un problema entrenando tu bot. Continuará funcionando con la base de conocimiento anterior.'}
              <br />
              <Typography
                variant="caption"
                style={{marginTop: '8px', display: 'block'}}
              >
                Si el problema persiste, contacta con soporte técnico.
              </Typography>
            </Alert>
          </Grid>
        )}

        <TrainingSection chatBot={chatBot} />

        <DeviceLinkingSection
          chatBot={chatBot}
          onSaveSuccess={refetchChatBot}
        />

        <ScheduleSection chatBot={chatBot} onSaveSuccess={refetchChatBot} />
      </Grid>
    </div>
  );
}
